import { useEffect, useState } from "react";
import { List, X, UserCircle } from "@phosphor-icons/react/dist/ssr";
import { useLocation } from "wouter";
import { LanguageSwitcher } from "@/components/language-switcher";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/contexts/LanguageContext";
import { getLaunchUrl } from "@/app-config";
import { EARLY_ACCESS_MODAL_HREF, openEarlyAccessModal } from "@/lib/early-access";

export function Navigation() {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [location, navigate] = useLocation();

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNavClick = (e: React.MouseEvent, href: string) => {
    e.preventDefault();

    if (href === EARLY_ACCESS_MODAL_HREF) {
      openEarlyAccessModal();
      setIsMobileMenuOpen(false);
      return;
    }

    if (href.startsWith('#')) {
      const targetId = href.substring(1);

      const pathParts = location.split('/').filter(Boolean);
      const isLanguagePrefix = pathParts[0] && ['de', 'fr', 'it'].includes(pathParts[0]);
      const isOnHome = (!isLanguagePrefix && location === '/') || (isLanguagePrefix && pathParts.length === 1);

      if (!isOnHome) {
        const langPrefix = currentLanguage !== 'en' ? `/${currentLanguage}` : '';
        navigate(langPrefix + '/' + href);
        return;
      }

      const targetElement = document.getElementById(targetId);
      if (targetElement) {
        const headerHeight = 72;
        const elementPosition = targetElement.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerHeight;
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        setIsMobileMenuOpen(false);
      }
    }
  };

  useEffect(() => {
    const pathParts = location.split('/').filter(Boolean);
    const isLanguagePrefix = pathParts[0] && ['de', 'fr', 'it'].includes(pathParts[0]);
    const isOnHome = (!isLanguagePrefix && location === '/') || (isLanguagePrefix && pathParts.length === 1);

    if (isOnHome && window.location.hash) {
      const targetId = window.location.hash.substring(1);
      setTimeout(() => {
        const targetElement = document.getElementById(targetId);
        if (targetElement) {
          const headerHeight = 72;
          const elementPosition = targetElement.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerHeight;
          window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location]);

  // Reduced to 5 core links
  const navLinks = [
    { href: "#who-for", label: t('navigation.solutions'), ctaId: "navbar_solutions" },
    { href: "#product-tour", label: t('navigation.features'), ctaId: "navbar_features" },
    { href: "#security", label: t('navigation.security'), ctaId: "navbar_security" },
    { href: "#pricing", label: t('navigation.pricing'), ctaId: "navbar_pricing" },
    { href: EARLY_ACCESS_MODAL_HREF, label: t('navigation.contact'), ctaId: "navbar_contact" },
  ];

  return (
    <header className="p-0 m-0">
      <nav
        className={`fixed top-0 w-full z-50 transition-all duration-300 ${
          isScrolled
            ? 'nav-scrolled border-b border-gray-200/60'
            : 'bg-transparent'
        }`}
        aria-label={t('navigation.primaryAria')}
      >
        <div className="container mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-[68px]">
            {/* Logo */}
            <div className="flex-shrink-0">
              <a
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  const langPrefix = currentLanguage !== 'en' ? `/${currentLanguage}` : '';
                  navigate(langPrefix + '/');
                }}
                className="flex items-center hover:opacity-75 transition-opacity cursor-pointer"
              >
                <img
                  src="/brand/logo-wordmark-red.svg"
                  alt="Rugira"
                  width="108"
                  height="34"
                  className="h-8 w-auto"
                />
              </a>
            </div>

            {/* Desktop Nav Links */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href)}
                  className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap"
                  data-cta-id={link.ctaId}
                  data-cta-section="navigation"
                  data-cta-label={link.label}
                >
                  {link.label}
                </a>
              ))}
            </div>

            {/* Desktop Right Actions */}
            <div className="hidden md:flex items-center gap-3 flex-shrink-0">
              <LanguageSwitcher />
              <a href={getLaunchUrl()}>
                <button
                  className="text-sm font-medium p-2 text-gray-500 hover:text-gray-800 hover:bg-gray-100/80 rounded-lg transition-all"
                  aria-label={t('navigation.login')}
                >
                  <UserCircle size={20} weight="regular" />
                </button>
              </a>
            </div>

            {/* Mobile hamburger */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100/80 transition-colors flex-shrink-0"
              aria-label={t('navigation.toggleMenu')}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? (
                <X size={22} weight="bold" style={{ color: 'var(--brand-red)' }} />
              ) : (
                <List size={22} weight="bold" style={{ color: 'var(--text-700)' }} />
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile drawer */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <div className="fixed top-[68px] left-0 right-0 bg-white border-b border-gray-200 shadow-xl">
            <div className="px-6 py-6 space-y-1">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href)}
                  className="block py-3 text-base font-medium text-gray-700 hover:text-gray-900 border-b border-gray-50 last:border-0 cursor-pointer"
                  data-cta-id={link.ctaId}
                >
                  {link.label}
                </a>
              ))}

              <div className="pt-4 space-y-3">
                <a href={getLaunchUrl()} className="block">
                  <button
                    className="w-full py-3 text-sm font-medium text-gray-600 hover:text-gray-900 flex items-center justify-center gap-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <UserCircle size={18} weight="regular" />
                    {t('navigation.login')}
                  </button>
                </a>
              </div>

              <div className="pt-3 pb-2 flex justify-center">
                <LanguageSwitcher />
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
